import { createSlice } from '@reduxjs/toolkit';

// Capital Pack State Management - Portfolio Intelligence Platform
// Manages portfolio companies, KPIs, alerts, and analytics for PE firms

const initialState = {
  // Portfolio Companies
  companies: [],
  
  // Portfolio KPIs and Metrics
  portfolioKPIs: {
    totalValue: 0,
    totalCompanies: 0,
    avgIRR: 0,
    avgMOIC: 0,
    trends: {
      revenue: 0,
      ebitda: 0,
      runway: 0
    }
  },
  
  // Monthly KPI data structure for cross-pack aggregation
  monthlyKPIs: [],
  
  // AI Insights and Alerts
  alerts: [],
  insights: [],
  
  // Data connectors and integrations
  connectors: [],
  
  // Thresholds and settings
  thresholds: {
    runwayFloor: 6, // months
    dscrFloor: 1.2,
    growthFloor: 10, // percentage
    varianceBands: {
      low: 5,
      medium: 15,
      high: 25
    }
  },
  
  // UI State
  loading: false,
  error: null,
  filters: {
    sector: 'All',
    region: 'All',
    stage: 'All',
    dateRange: '12M'
  }
};

const capitalSlice = createSlice({
  name: 'capital',
  initialState,
  reducers: {
    // Company Management
    setCompanies: (state, action) => {
      state.companies = action?.payload;
    },
    
    addCompany: (state, action) => {
      state?.companies?.push(action?.payload);
    },
    
    updateCompany: (state, action) => {
      const { id, updates } = action?.payload;
      const companyIndex = state?.companies?.findIndex(company => company?.id === id);
      if (companyIndex !== -1) {
        state.companies[companyIndex] = { ...state?.companies?.[companyIndex], ...updates };
      }
    },
    
    // Portfolio KPIs
    setPortfolioKPIs: (state, action) => {
      state.portfolioKPIs = action?.payload;
    },
    
    updatePortfolioKPI: (state, action) => {
      const { metric, value } = action?.payload;
      state.portfolioKPIs[metric] = value;
    },
    
    // Monthly KPI Management
    setMonthlyKPIs: (state, action) => {
      state.monthlyKPIs = action?.payload;
    },
    
    addMonthlyKPI: (state, action) => {
      state?.monthlyKPIs?.push(action?.payload);
    },
    
    updateMonthlyKPI: (state, action) => {
      const { companyId, periodMonth, updates } = action?.payload;
      const kpiIndex = state?.monthlyKPIs?.findIndex(
        kpi => kpi?.companyId === companyId && kpi?.periodMonth === periodMonth
      );
      if (kpiIndex !== -1) {
        state.monthlyKPIs[kpiIndex] = { ...state?.monthlyKPIs?.[kpiIndex], ...updates };
      }
    },
    
    // Alerts Management
    setAlerts: (state, action) => {
      state.alerts = action?.payload;
    },
    
    addAlert: (state, action) => {
      state?.alerts?.push({
        ...action?.payload,
        id: Date.now(),
        ts: new Date()?.toISOString(),
        status: 'active'
      });
    },
    
    updateAlert: (state, action) => {
      const { id, updates } = action?.payload;
      const alertIndex = state?.alerts?.findIndex(alert => alert?.id === id);
      if (alertIndex !== -1) {
        state.alerts[alertIndex] = { ...state?.alerts?.[alertIndex], ...updates };
      }
    },
    
    resolveAlert: (state, action) => {
      const alertId = action?.payload;
      const alertIndex = state?.alerts?.findIndex(alert => alert?.id === alertId);
      if (alertIndex !== -1) {
        state.alerts[alertIndex].status = 'resolved';
      }
    },
    
    // AI Insights
    setInsights: (state, action) => {
      state.insights = action?.payload;
    },
    
    addInsight: (state, action) => {
      state?.insights?.push({
        ...action?.payload,
        id: Date.now(),
        timestamp: new Date()?.toISOString()
      });
    },
    
    // Connectors and Integrations
    setConnectors: (state, action) => {
      state.connectors = action?.payload;
    },
    
    updateConnector: (state, action) => {
      const { companyId, system, updates } = action?.payload;
      const connectorIndex = state?.connectors?.findIndex(
        connector => connector?.companyId === companyId && connector?.system === system
      );
      if (connectorIndex !== -1) {
        state.connectors[connectorIndex] = { ...state?.connectors?.[connectorIndex], ...updates };
      } else {
        state?.connectors?.push({
          companyId,
          system,
          ...updates,
          lastSyncAt: new Date()?.toISOString()
        });
      }
    },
    
    // Thresholds and Settings
    setThresholds: (state, action) => {
      state.thresholds = { ...state?.thresholds, ...action?.payload };
    },
    
    updateThreshold: (state, action) => {
      const { key, value } = action?.payload;
      state.thresholds[key] = value;
    },
    
    // Filters
    setFilters: (state, action) => {
      state.filters = { ...state?.filters, ...action?.payload };
    },
    
    updateFilter: (state, action) => {
      const { key, value } = action?.payload;
      state.filters[key] = value;
    },
    
    resetFilters: (state) => {
      state.filters = {
        sector: 'All',
        region: 'All',
        stage: 'All',
        dateRange: '12M'
      };
    },
    
    // Loading and Error States
    setLoading: (state, action) => {
      state.loading = action?.payload;
    },
    
    setError: (state, action) => {
      state.error = action?.payload;
    },
    
    clearError: (state) => {
      state.error = null;
    },
    
    // NEW: Seeder integration actions for Step 3
    loadDunportDemo: (state, action) => {
      const { companies, monthlyKPIs, alerts, insights, thresholds } = action?.payload;
      
      state.companies = companies;
      state.monthlyKPIs = monthlyKPIs;
      state.alerts = alerts;
      state.insights = insights;
      state.thresholds = { ...state?.thresholds, ...thresholds };
      state.loading = false;
      state.error = null;
    },
    
    seedFromPacks: (state, action) => {
      // Action to trigger seeding from existing pack data
      state.loading = true;
      state.error = null;
    },
    
    clearPortfolioData: (state) => {
      // Reset action for demo controls
      state.companies = [];
      state.monthlyKPIs = [];
      state.alerts = [];
      state.insights = [];
      state.portfolioKPIs = initialState?.portfolioKPIs;
      state.loading = false;
      state.error = null;
    },
    
    updateFromPackData: (state, action) => {
      const { packType, companyId, packData, month } = action?.payload;
      
      // This will be handled by the adapters in the seeder
      // Mark as loading while processing
      state.loading = true;
    }
  }
});

export const {
  // Company actions
  setCompanies,
  addCompany,
  updateCompany,
  
  // Portfolio KPI actions
  setPortfolioKPIs,
  updatePortfolioKPI,
  
  // Monthly KPI actions
  setMonthlyKPIs,
  addMonthlyKPI,
  updateMonthlyKPI,
  
  // Alert actions
  setAlerts,
  addAlert,
  updateAlert,
  resolveAlert,
  
  // Insight actions
  setInsights,
  addInsight,
  
  // Connector actions
  setConnectors,
  updateConnector,
  
  // Settings actions
  setThresholds,
  updateThreshold,
  
  // Filter actions
  setFilters,
  updateFilter,
  resetFilters,
  
  // UI actions
  setLoading,
  setError,
  clearError,
  
  // NEW: Step 3 seeder actions
  loadDunportDemo,
  seedFromPacks,
  clearPortfolioData,
  updateFromPackData
} = capitalSlice?.actions;

// Selectors for computed values and derived state

// Company selectors
export const selectAllCompanies = (state) => state?.capital?.companies || [];
export const selectCompanyById = (companyId) => (state) => 
  state?.capital?.companies?.find(company => company?.id === companyId);

// Portfolio metrics selectors
export const selectPortfolioKPIs = (state) => state?.capital?.portfolioKPIs || initialState?.portfolioKPIs;
export const selectPortfolioValue = (state) => state?.capital?.portfolioKPIs?.totalValue || 0;
export const selectCompanyCount = (state) => state?.capital?.companies?.length || 0;

// Health Score Calculator - Core PE Intelligence Feature
export const selectHealthScore = (companyId, month) => (state) => {
  const company = selectCompanyById(companyId)(state);
  const kpis = state?.capital?.monthlyKPIs?.find(
    kpi => kpi?.companyId === companyId && kpi?.periodMonth === month
  );
  
  if (!company || !kpis) return { score: 0, reasons: ['Insufficient data'] };
  
  let score = 0;
  const reasons = [];
  
  // Revenue Health (25 points)
  if (kpis?.growthYoY >= 20) {
    score += 25;
    reasons?.push('Strong revenue growth');
  } else if (kpis?.growthYoY >= 10) {
    score += 20;
    reasons?.push('Moderate revenue growth');
  } else if (kpis?.growthYoY >= 0) {
    score += 10;
    reasons?.push('Stable revenue');
  } else {
    reasons?.push('Revenue decline concern');
  }
  
  // Profitability Health (25 points)
  if (kpis?.ebitdaMargin >= 25) {
    score += 25;
    reasons?.push('Excellent profitability');
  } else if (kpis?.ebitdaMargin >= 15) {
    score += 20;
    reasons?.push('Good profitability');
  } else if (kpis?.ebitdaMargin >= 5) {
    score += 10;
    reasons?.push('Moderate profitability');
  } else {
    reasons?.push('Profitability concern');
  }
  
  // Liquidity Health (25 points)
  if (kpis?.runwayMonths >= 18) {
    score += 25;
    reasons?.push('Strong cash position');
  } else if (kpis?.runwayMonths >= 12) {
    score += 20;
    reasons?.push('Adequate cash runway');
  } else if (kpis?.runwayMonths >= 6) {
    score += 10;
    reasons?.push('Limited cash runway');
  } else {
    reasons?.push('Critical cash shortage');
  }
  
  // Covenant Health (25 points)
  if (kpis?.covenantOk && kpis?.dscr >= 1.5) {
    score += 25;
    reasons?.push('Strong covenant compliance');
  } else if (kpis?.covenantOk && kpis?.dscr >= 1.2) {
    score += 20;
    reasons?.push('Covenant compliant');
  } else if (kpis?.dscr >= 1.0) {
    score += 10;
    reasons?.push('Covenant risk emerging');
  } else {
    reasons?.push('Covenant breach risk');
  }
  
  return {
    score: Math.min(100, Math.max(0, score)),
    reasons: reasons?.slice(0, 3) // Top 3 reasons
  };
};

// Filtered companies based on current filters
export const selectFilteredCompanies = (state) => {
  const companies = selectAllCompanies(state);
  const filters = state?.capital?.filters || initialState?.filters;
  
  return companies?.filter(company => {
    if (filters?.sector !== 'All' && company?.sector !== filters?.sector) return false;
    if (filters?.region !== 'All' && company?.region !== filters?.region) return false;
    if (filters?.stage !== 'All' && company?.stage !== filters?.stage) return false;
    return true;
  });
};

// Active alerts and insights
export const selectActiveAlerts = (state) => 
  state?.capital?.alerts?.filter(alert => alert?.status === 'active') || [];

export const selectRecentInsights = (state) => 
  state?.capital?.insights?.slice(-10) || [];

// NEW: Demo-specific selectors for Step 3
export const selectPortfolioSummary = (state) => {
  const companies = selectAllCompanies(state);
  const kpis = state?.capital?.monthlyKPIs || [];
  
  if (companies?.length === 0) return null;
  
  // Calculate portfolio-level metrics
  const latestKPIs = companies?.map(company => {
    const companyKPIs = kpis?.filter(k => k?.companyId === company?.id)?.sort((a, b) => new Date(b?.month) - new Date(a?.month));
    return companyKPIs?.[0]; // Most recent KPI
  })?.filter(Boolean);
  
  const totalRevenue = latestKPIs?.reduce((sum, k) => sum + (k?.revenue || 0), 0);
  const totalEbitda = latestKPIs?.reduce((sum, k) => sum + (k?.ebitda || 0), 0);
  const avgMargin = totalRevenue > 0 ? (totalEbitda / totalRevenue) * 100 : 0;
  const avgRisk = latestKPIs?.reduce((sum, k) => sum + (k?.riskScore || 0), 0) / latestKPIs?.length;
  
  return {
    totalRevenue: `£${(totalRevenue / 1000000)?.toFixed(1)}M`,
    totalEbitda: `£${(totalEbitda / 1000000)?.toFixed(1)}M`,
    avgEbitdaMargin: `${avgMargin?.toFixed(1)}%`,
    portfolioHealth: avgRisk?.toFixed(0),
    companyCount: companies?.length,
    sectorMix: companies?.reduce((acc, c) => {
      acc[c?.sector] = (acc?.[c?.sector] || 0) + 1;
      return acc;
    }, {})
  };
};

export const selectSectorPerformance = (state) => {
  const companies = selectAllCompanies(state);
  const kpis = state?.capital?.monthlyKPIs || [];
  const sectors = [...new Set(companies?.map(c => c?.sector))];
  
  return sectors?.map(sector => {
    const sectorCompanies = companies?.filter(c => c?.sector === sector);
    const sectorKPIs = kpis?.filter(k => sectorCompanies?.some(c => c?.id === k?.companyId));
    
    const avgRevenue = sectorKPIs?.reduce((sum, k) => sum + (k?.revenue || 0), 0) / Math.max(sectorKPIs?.length, 1);
    const avgMargin = sectorKPIs?.reduce((sum, k) => sum + (k?.ebitdaMargin || 0), 0) / Math.max(sectorKPIs?.length, 1);
    const avgRisk = sectorKPIs?.reduce((sum, k) => sum + (k?.riskScore || 0), 0) / Math.max(sectorKPIs?.length, 1);
    
    return {
      sector,
      companyCount: sectorCompanies?.length,
      avgRevenue: avgRevenue || 0,
      avgMargin: avgMargin || 0,
      avgRisk: avgRisk || 0,
      totalRevenue: sectorKPIs?.reduce((sum, k) => sum + (k?.revenue || 0), 0) || 0
    };
  });
};

export const selectTopPerformers = (state) => {
  const companies = selectAllCompanies(state);
  const kpis = state?.capital?.monthlyKPIs || [];
  
  return companies?.map(company => {
    const latestKPI = kpis?.filter(k => k?.companyId === company?.id)?.sort((a, b) => new Date(b?.month) - new Date(a?.month))?.[0];
    
    return {
      ...company,
      latestKPI,
      performanceScore: latestKPI?.riskScore || 0
    };
  })?.sort((a, b) => (b?.performanceScore || 0) - (a?.performanceScore || 0))?.slice(0, 5);
};

export const selectCompaniesAtRisk = (state) => {
  const companies = selectAllCompanies(state);
  const kpis = state?.capital?.monthlyKPIs || [];
  const thresholds = state?.capital?.thresholds || initialState?.thresholds;
  
  return companies?.filter(company => {
    const latestKPI = kpis?.filter(k => k?.companyId === company?.id)?.sort((a, b) => new Date(b?.month) - new Date(a?.month))?.[0];
    
    if (!latestKPI) return false;
    
    return (
      latestKPI?.runway < thresholds?.runwayFloor ||
      latestKPI?.dscr < thresholds?.dscrFloor ||
      latestKPI?.riskScore < 60
    );
  })?.map(company => {
    const latestKPI = kpis?.filter(k => k?.companyId === company?.id)?.sort((a, b) => new Date(b?.month) - new Date(a?.month))?.[0];
    return { ...company, latestKPI };
  });
};

export const selectPortfolioTrends = (state) => {
  const { companies, monthlyKPIs } = state?.capital;
  
  if (!companies || companies?.length === 0) {
    return {
      byMonth: [],
      bySector: {},
      aggregated: []
    };
  }

  // Group monthly KPIs by month
  const kpisByMonth = {};
  
  companies?.forEach(company => {
    const companyKPIs = monthlyKPIs?.[company?.id] || [];
    
    companyKPIs?.forEach(kpi => {
      if (!kpi?.month) return;
      
      if (!kpisByMonth?.[kpi?.month]) {
        kpisByMonth[kpi.month] = {
          month: kpi?.month,
          companies: [],
          totalRevenue: 0,
          totalEbitda: 0,
          avgEbitdaMargin: 0,
          avgDscr: 0,
          companiesCount: 0
        };
      }
      
      kpisByMonth?.[kpi?.month]?.companies?.push({
        companyId: company?.id,
        companyName: company?.name,
        sector: company?.sector,
        revenue: kpi?.revenue || 0,
        ebitda: kpi?.ebitda || 0,
        ebitdaMargin: kpi?.ebitdaMargin || 0,
        dscr: kpi?.dscr || 0
      });
      
      kpisByMonth[kpi.month].totalRevenue += (kpi?.revenue || 0);
      kpisByMonth[kpi.month].totalEbitda += (kpi?.ebitda || 0);
      kpisByMonth[kpi.month].companiesCount += 1;
    });
  });

  // Calculate averages
  Object.keys(kpisByMonth)?.forEach(month => {
    const data = kpisByMonth?.[month];
    const count = data?.companiesCount || 1;
    
    data.avgEbitdaMargin = data?.companies?.reduce((sum, c) => sum + (c?.ebitdaMargin || 0), 0) / count;
    data.avgDscr = data?.companies?.reduce((sum, c) => sum + (c?.dscr || 0), 0) / count;
  });

  // Group by sector
  const bySector = {};
  companies?.forEach(company => {
    if (!bySector?.[company?.sector]) {
      bySector[company.sector] = {
        sector: company?.sector,
        companies: [],
        avgValuation: 0,
        avgEbitdaMargin: 0,
        totalRevenue: 0
      };
    }
    
    bySector?.[company?.sector]?.companies?.push(company);
    bySector[company.sector].avgValuation += (company?.valuation || 0);
    bySector[company.sector].avgEbitdaMargin += (company?.ebitdaMargin || 0);
    bySector[company.sector].totalRevenue += (company?.revenue || 0);
  });

  // Calculate sector averages
  Object.keys(bySector)?.forEach(sector => {
    const count = bySector?.[sector]?.companies?.length || 1;
    bySector[sector].avgValuation = bySector?.[sector]?.avgValuation / count;
    bySector[sector].avgEbitdaMargin = bySector?.[sector]?.avgEbitdaMargin / count;
  });

  // Convert to arrays sorted by month
  const byMonth = Object.values(kpisByMonth)?.sort((a, b) => a?.month?.localeCompare(b?.month));

  return {
    byMonth,
    bySector,
    aggregated: byMonth
  };
};

export default capitalSlice?.reducer;